// Cache identities for the `evidence/library`, `evidence/source` and
// `evidence/leaderboard` families.
//
// Frozen VERBATIM from their two pre-move definition sites:
//   `src/components/library/hooks/queryKeys.ts`                  (collections)
//   `src/components/library/source-lists/hooks/useSourceLists.ts` (source lists)
// Both old paths are now re-export shims, so there is exactly ONE declaration
// per identity and `migration:check`'s `key-single-site` guard fails the build
// if either literal is ever re-declared under `src/**`.
//
// WHY ONE MODULE FOR BOTH RESOURCES. The ARCFE-1049 partition ruling made
// `evidence/library` one family, not two, on the strength of a WRITE that
// crosses: `useImportSourceList`'s `onSuccess` invalidates
// `COLLECTIONS_QUERY_KEY` alongside the two source-list keys, so a source-list
// write reconciles the collections cache. Splitting the keys across two
// modules would either duplicate a definition site or manufacture a
// package-to-package import created solely to name another resource's key —
// the two outcomes that ruling exists to prevent.
//
// The file keeps the basename of the module that moved into it
// (`queryKeys.ts`, not the `keys.ts` most sibling packages use): a moved file
// keeps its original basename, and the three source-list constants were
// carved out of a hook body rather than moved as a file.

// ===== COLLECTIONS (`/evidence/collections`) =====

export const COLLECTIONS_QUERY_KEY = 'library-collections';
export const COLLECTION_DETAIL_QUERY_KEY = 'library-collection-detail';

// ===== USER DOCUMENTS (`/evidence/user-documents`) =====
//
// Frozen VERBATIM from the three hook modules that declared them inline, and
// they keep their three disagreeing spellings — SCREAMING_SNAKE, kebab-case
// and SCREAMING_SNAKE again. The string IS the cache, so normalising any of
// them is a silent cold start for the library grid, the preview rail and the
// chat document picker at deploy, while reading as a rename in review.
//
// They are in the SAME module as the collections keys rather than a
// `user-documents` one of their own for the reason the header records for
// collections and source lists: every document write invalidates
// `COLLECTIONS_QUERY_KEY` and `COLLECTION_DETAIL_QUERY_KEY` alongside its own
// two, so a document write reconciles the collections cache and the three
// resources are one family.

/** `GET /evidence/user-documents/{id}/download-url` — the presigned read. */
export const DOCUMENT_DOWNLOAD_URL_KEY = 'DOCUMENT_DOWNLOAD_URL';

/**
 * `GET /evidence/user-documents/all` — the jobs-and-documents list the library
 * grid renders.
 *
 * `src/features/workflows/components/useWorkflowContextDocumentUpload.ts`
 * hard-codes a SECOND spelling of this identity (`['documents-and-jobs']`) at
 * its own invalidation. The two literals agree, so the invalidation works; the
 * duplicate is pre-existing and is left where it is rather than hoisted here,
 * because hoisting it would put a workflows file on this door for one string.
 */
export const DOCUMENTS_AND_JOBS_KEY = 'documents-and-jobs';

/** `GET /evidence/user-documents` — the completed-documents list, by scope. */
export const GET_USER_DOCUMENTS = 'GET_USER_DOCUMENTS';

// ===== SOURCE LISTS (`/source-lists`) =====

export const SOURCE_LISTS_QUERY_KEY = 'source-lists';
export const SOURCE_LIST_DETAIL_QUERY_KEY = 'source-list-detail';

/**
 * Unmigrated (not-yet-converted-to-collection) source lists — the read behind
 * `IS_UNIFIED_COLLECTIONS_ENABLED`. A distinct identity from
 * `SOURCE_LISTS_QUERY_KEY`, not a prefix of it: `useImportSourceList`
 * invalidates both explicitly, and collapsing them would drop one refresh.
 */
export const UNMIGRATED_SOURCE_LISTS_QUERY_KEY = 'source-lists-unmigrated';

// ===== `evidence/source` =====
//
// Frozen VERBATIM from the five hook modules that declared them inline. Every
// old path now re-exports the identity it used to declare, so there is still
// exactly ONE declaration site per cache and `migration:check`'s
// `key-single-site` guard fails the build if any literal reappears under
// `src/**`.
//
// The six identities are deliberately NOT normalised into one factory shape.
// Three are bare strings composed into a tuple at the `useQuery` call, one is a
// mutation key, and one is ALREADY an array at its declaration site — and that
// difference is what the consumers write today. A factory would be a nicer
// surface and a different cache.

/**
 * `/transcript-chips/config` — the auto-evidence read.
 *
 * A camelCase identity where its siblings are kebab-case, and it stays that way:
 * the string is the cache, not a name, and a tidy-up here is a silent cold
 * start for every signed-in clinician's chip rail and pinned shortcuts.
 */
export const GET_EVIDENCE_CONFIG = 'getEvidenceConfig';

/**
 * The auto-evidence config WRITE.
 *
 * Was a module-private const in `src/hooks/mutation/useMutateEvidenceConfig.ts`.
 * It is published here rather than kept private in the carved hook because a
 * mutation key is a cache identity like any other — `useMutationState` can
 * select on it — and the frozen-key law has no expression for an identity that
 * is exempt from single-site declaration because it happens to be unexported.
 */
export const UPDATE_EVIDENCE_CONFIG = 'updateEvidenceConfig';

/** `/evidence/source-recommendations` — country/specialty-matched sources. */
export const SOURCE_RECOMMENDATIONS_QUERY_KEY = 'source-recommendations';

/**
 * `/chat/sources` — the static source catalogue every source picker reads.
 *
 * A `chat_`-prefixed identity in an evidence key module, and it stays that way.
 * The string IS the cache, so re-minting it as `evidence-sources` would cold
 * start every source picker and every domain-label lookup at deploy while
 * looking like a rename in review. The prefix records where the route lives,
 * not who owns the read.
 *
 * The declaration is a BARE STRING composed into `[GET_SOURCES]` at the one
 * `useQuery` call, which is the pre-move shape rather than a choice made here —
 * see the note above about why the five identities are not normalised into one
 * factory.
 */
export const GET_SOURCES = 'chat_sources';

/**
 * `/chat/blocked-domains` — the content-safety denylist behind the source
 * pickers.
 *
 * The second `chat_`-prefixed identity in this module, arriving for the same
 * reason and on the same ruling as `GET_SOURCES`: the route says chat, every
 * consumer is an evidence source picker, and the owner ruling moved the code
 * without re-minting the string. It was the family's last row in
 * `cache-key-baseline.json`, and the row leaves with the declaration.
 */
export const GET_BLOCKED_DOMAINS = 'chat_blocked_domains';

/**
 * `/chat/capabilities` — what Evidence chat can do in this deployment
 * (MOD-1726). `chat_`-prefixed after its route, like the two above.
 */
export const GET_CHAT_CAPABILITIES = 'chat_capabilities';

/** `/evidence/{sessionId}/summary-suggestion` — the banner's clinical signal. */
export const EVIDENCE_SUMMARY_SUGGESTION_QUERY_KEY = 'evidence-summary-suggestion';

/**
 * `/chat/personalized-suggestions` — recent-work-informed starters.
 *
 * AN ARRAY, not a string, and that is the measured shape rather than a choice
 * made here: the pre-move declaration was
 * `['personalized-evidence-suggestions'] as const` and both consumers
 * (`useSessionlessEvidenceAwarenessPopover` and the carved read) pass it
 * straight to `queryKey` / `invalidateQueries`. Unwrapping it to a bare string
 * would compile, keep the same literal, and change nothing visible in review
 * while every existing consumer started keying on a nested array.
 */
export const PERSONALIZED_EVIDENCE_SUGGESTIONS_QUERY_KEY = [
  'personalized-evidence-suggestions',
] as const;

// ===== `evidence/leaderboard` =====
//
// Frozen VERBATIM from the six hook modules that declared them inline. Four
// were already exported and two (`GENERATE_EVIDENCE_REFERRAL_LINK`,
// `UPDATE_LEADERBOARD_SETTINGS`) were module-private mutation keys, published
// here for the reason `UPDATE_EVIDENCE_CONFIG` was: a mutation key is a cache
// identity like any other — `useMutationState` selects on it — and the
// frozen-key law has no expression for an identity exempt from single-site
// declaration because it happens to be unexported.
//
// THE SPELLINGS DISAGREE WITH EACH OTHER AND ALL OF THEM STAY. Three are
// snake_case (`get_evidence_leaderboard`), two are camelCase
// (`evidenceUsageStats`), two are SCREAMING_SNAKE (`UPDATE_LEADERBOARD_SETTINGS`)
// — the string is the cache, not a name, and normalising any of them is a
// silent cold start for whichever surface reads it.

/** `GET /competitions/periods/{period}` — the queries/referrals leaderboards. */
export const GET_EVIDENCE_LEADERBOARD = 'get_evidence_leaderboard';

/** `GET /competitions/{slug}/leaderboard` — one competition, by slug or series. */
export const GET_COMPETITION_LEADERBOARD = 'get_competition_leaderboard';

/** `GET /evidence-referrals/stats` — the signed-in clinician's rank and counts. */
export const GET_MY_EVIDENCE_STATS = 'get_my_evidence_stats';

/** `GET /evidence-referrals/my-referrals` — who this clinician has referred. */
export const GET_MY_REFERRALS = 'get_my_referrals';

/** `GET /user-activity/evidence` — per-user Evidence usage, for the trial recap. */
export const EVIDENCE_USAGE_STATS_QUERY_KEY = 'evidenceUsageStats';

/**
 * `GET /user-activity/team/{team_id}/evidence` — the same aggregate summed
 * across a team.
 *
 * A distinct identity from `EVIDENCE_USAGE_STATS_QUERY_KEY` rather than a
 * variant of it: the team read carries `{ teamId }` in its tuple, and one
 * clinician can be an admin of a team whose numbers differ from their own.
 */
export const TEAM_EVIDENCE_USAGE_STATS_QUERY_KEY = 'teamEvidenceUsageStats';

/** `POST /evidence-referrals/link` — the referral-link WRITE. */
export const GENERATE_EVIDENCE_REFERRAL_LINK = 'GENERATE_EVIDENCE_REFERRAL_LINK';

/** `PATCH /evidence-referrals/settings` — the leaderboard-visibility WRITE. */
export const UPDATE_LEADERBOARD_SETTINGS = 'UPDATE_LEADERBOARD_SETTINGS';

// ===== `evidence/guidance` =====
//
// Both were module-private consts in their own hook files, published here for
// the reason `UPDATE_EVIDENCE_CONFIG` and the two leaderboard write keys were: a
// mutation key is a cache identity like any other — `useMutationState` selects
// on it — and the frozen-key law has no expression for an identity that is
// exempt from single-site declaration because it happens to be unexported.
//
// THE FAMILY DECLARES NO READ KEY, and that absence is the shape of the family
// rather than an omission. Its one read derives from the integrations list,
// whose identity (`GET_EVIDENCE_INTEGRATIONS`) is the INTEGRATIONS lane's and
// stays declared there — the carve is careful never to name it package-side.

/** `POST /evidence/healthpathways/setup` — the zone-setup WRITE. */
export const SETUP_HEALTHPATHWAYS = 'SetupHealthPathways';

/**
 * `DELETE /evidence/healthpathways/setup` — the zone-removal WRITE.
 *
 * A distinct identity from `SETUP_HEALTHPATHWAYS` even though both writes are
 * the same route with different verbs: they are two mutations, and a
 * `useMutationState` selector on one must not match the other.
 */
export const REMOVE_HEALTHPATHWAYS = 'RemoveHealthPathways';
